import { useState, useEffect, useCallback } from 'react';
import { Search, Inbox, FolderOpen, Users, User, X, Sparkles } from 'lucide-react';
import { OrbitIcon } from "@/components/icons/OrbitIcon";
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useNavigate } from 'react-router-dom';
import { useInquiries } from '@/services/inquiries';
import { useProjects } from '@/services/projects';
import { useClients } from '@/services/clients';
import { useCreators } from '@/services/creators';
import { cn } from '@/lib/utils';

const EMPTY: any[] = [];

interface SearchResult {
  id: string;
  type: 'inquiry' | 'project' | 'client' | 'creator';
  title: string;
  subtitle?: string;
  path: string;
}

interface GlobalSearchProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function GlobalSearch({ open, onOpenChange }: GlobalSearchProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const navigate = useNavigate();

  // IMPORTANT: avoid `data: x = []` defaults here — that creates a brand new array each render,
  // which changes dependencies and can cause an infinite update loop.
  const { data: inquiriesData } = useInquiries();
  const { data: projectsData } = useProjects();
  const { data: clientsData } = useClients();
  const { data: creatorsData } = useCreators();

  const inquiries = inquiriesData ?? EMPTY;
  const projects = projectsData ?? EMPTY;
  const clients = clientsData ?? EMPTY;
  const creators = creatorsData ?? EMPTY;

  const search = useCallback((searchQuery: string) => {
    if (!searchQuery.trim()) {
      setResults([]);
      return;
    }

    const q = searchQuery.toLowerCase();
    const searchResults: SearchResult[] = [];

    // Search inquiries
    inquiries.forEach((inquiry) => {
      if (
        inquiry.name.toLowerCase().includes(q) ||
        inquiry.email.toLowerCase().includes(q)
      ) {
        searchResults.push({
          id: inquiry.id,
          type: 'inquiry',
          title: inquiry.name,
          subtitle: inquiry.email,
          path: '/', // Navigate to dashboard with inquiry selected
        });
      }
    });

    // Search projects
    projects.forEach((project) => {
      if (project.name.toLowerCase().includes(q)) {
        searchResults.push({
          id: project.id,
          type: 'project',
          title: project.name,
          subtitle: project.status?.replace('_', ' '),
          path: `/studio/${project.id}`,
        });
      }
    });

    // Search clients
    clients.forEach((client) => {
      if (
        client.name.toLowerCase().includes(q) ||
        (client.email && client.email.toLowerCase().includes(q))
      ) {
        searchResults.push({
          id: client.id,
          type: 'client',
          title: client.name,
          subtitle: client.email || undefined,
          path: '/clients',
        });
      }
    });

    // Search creators
    creators.forEach((creator) => {
      if (
        creator.name.toLowerCase().includes(q) ||
        creator.email.toLowerCase().includes(q)
      ) {
        searchResults.push({
          id: creator.id,
          type: 'creator',
          title: creator.name,
          subtitle: `${creator.role} • ${creator.status}`,
          path: '/', // Navigate to dashboard creators section
        });
      }
    });

    setResults(searchResults.slice(0, 12));
    setSelectedIndex(0);
  }, [inquiries, projects, clients, creators]);

  useEffect(() => {
    search(query);
  }, [query, search]);

  useEffect(() => {
    if (!open) {
      setQuery('');
      setResults([]);
      setSelectedIndex(0);
    }
  }, [open]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => Math.min(prev + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && results[selectedIndex]) {
      e.preventDefault();
      navigate(results[selectedIndex].path);
      onOpenChange(false);
    }
  };

  const getIcon = (type: SearchResult['type']) => {
    switch (type) {
      case 'inquiry':
        return Inbox;
      case 'project':
        return FolderOpen;
      case 'client':
        return User;
      case 'creator':
        return Sparkles;
      default:
        return Users;
    }
  };

  const getTypeLabel = (type: SearchResult['type']) => {
    switch (type) {
      case 'inquiry':
        return 'Inquiry';
      case 'project':
        return 'Project';
      case 'client':
        return 'Client';
      case 'creator':
        return 'Creator';
      default:
        return '';
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl p-0 gap-0 overflow-hidden">
        <div className="flex items-center border-b border-border px-4">
          <OrbitIcon name="magnifyingglass" fallback={Search} className="w-5 h-5 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search inquiries, projects, clients..."
            className="border-0 focus-visible:ring-0 text-lg py-6 px-3"
            autoFocus
          />
          {query && (
            <button
              onClick={() => setQuery('')}
              className="p-1 hover:bg-muted rounded"
            >
              <OrbitIcon name="xmark" fallback={X} className="w-4 h-4 text-muted-foreground" />
            </button>
          )}
        </div>

        {results.length > 0 && (
          <div className="max-h-80 overflow-y-auto p-2">
            {results.map((result, index) => {
              const Icon = getIcon(result.type);
              return (
                <button
                  key={`${result.type}-${result.id}`}
                  onClick={() => {
                    navigate(result.path);
                    onOpenChange(false);
                  }}
                  className={cn(
                    'w-full flex items-center gap-3 px-3 py-3 rounded-lg text-left transition-colors',
                    index === selectedIndex
                      ? 'bg-primary/10 text-foreground'
                      : 'hover:bg-muted/50 text-foreground'
                  )}
                >
                  <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-secondary">
                    <Icon className="w-5 h-5 text-muted-foreground" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{result.title}</div>
                    {result.subtitle && (
                      <div className="text-sm text-muted-foreground truncate">
                        {result.subtitle}
                      </div>
                    )}
                  </div>
                  <span className="text-xs text-muted-foreground bg-secondary px-2 py-1 rounded">
                    {getTypeLabel(result.type)}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {query && results.length === 0 && (
          <div className="p-8 text-center text-muted-foreground">
            No results found for "{query}"
          </div>
        )}

        {!query && (
          <div className="p-6 text-center text-muted-foreground text-sm">
            <p>Start typing to search across all your data</p>
            <p className="mt-2 text-xs">
              <kbd className="px-2 py-1 bg-secondary rounded text-foreground">↑↓</kbd>
              {' '}to navigate {' '}
              <kbd className="px-2 py-1 bg-secondary rounded text-foreground">↵</kbd>
              {' '}to select {' '}
              <kbd className="px-2 py-1 bg-secondary rounded text-foreground">esc</kbd>
              {' '}to close
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Hook for keyboard shortcut
export function useGlobalSearch() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return { open, setOpen };
}
